import {
  ACTION,
  Card,
  CopyButton,
  type DataResponse,
  Dialog,
  type DialogProps,
  Divider,
  Flex,
  Get,
  HStack,
  IconName,
  isOrderPendingFix,
  MixpanelEvent,
  type Order,
  type Quote,
  request,
  Text,
  type Trade,
  useDisclosureWithContext,
  useEndpointsContext,
  useGlobalToasts,
  useMixpanel,
  VStack,
} from '@talos/kyoko';
import type { MenuItemDef } from 'ag-grid-community';
import { useCallback, useEffect, useState } from 'react';
import { useFeatureFlag, useRoleAuth } from '../../../../hooks';
import { useOrderInteractions } from '../../../../providers';

export const useForceCancelCareOrderChildMenuItem = () => {
  const { allowCancellingCareOrderChildOrder } = useFeatureFlag();
  const { isAuthorized } = useRoleAuth();
  const { orgApiEndpoint } = useEndpointsContext();
  const { add: addToast } = useGlobalToasts();
  const disclosure = useDisclosureWithContext<Order>();
  const allowCancelOrder = allowCancellingCareOrderChildOrder && isAuthorized(ACTION.FORCE_CANCEL_SPREADTOFIXING_ORDER);

  const { open } = disclosure;

  const getOrder = useCallback(
    (rfqid: string) => {
      if (!orgApiEndpoint) {
        return undefined;
      }

      return Get<DataResponse<Order>>(orgApiEndpoint, `/orders?RFQID=${rfqid}`)
        .then(res => {
          return res.data.at(0);
        })
        .catch((e: ErrorEvent) => {
          addToast({
            variant: 'NEGATIVE',
            text: `Failed to find order to cancel: ${e.message}`,
          });
        });
    },
    [orgApiEndpoint, addToast]
  );

  const openForceCancelOrderDialogUsingRFQID = useCallback(
    async (rfqid: string) => {
      const order = await getOrder(rfqid);
      if (order) {
        open(order);
      }
    },
    [open, getOrder]
  );

  const getForceCancelCareOrderChildQuoteItem = useCallback(
    (quote: Quote | undefined): MenuItemDef | undefined => {
      if (!quote) {
        return undefined;
      }

      const showItem = allowCancelOrder && quote.PricingMode === 'SpreadToFixing' && quote.QuoteStatus === 'PendingFix';
      if (!showItem) {
        return undefined;
      }

      return {
        name: 'Force cancel',
        action: () => openForceCancelOrderDialogUsingRFQID(quote.RFQID),
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      };
    },
    [openForceCancelOrderDialogUsingRFQID, allowCancelOrder]
  );

  const getForceCancelCareOrderChildOrderItem = useCallback(
    (orders: Order[]): MenuItemDef | undefined => {
      if (orders.length > 1) {
        return undefined;
      }

      const order = orders.at(0);
      if (!order) {
        return undefined;
      }

      const showItem = allowCancelOrder && order.PricingMode === 'SpreadToFixing' && isOrderPendingFix(order);
      if (!showItem) {
        return undefined;
      }

      return {
        name: 'Force cancel',
        action: () => open(order),
        icon: `<i class="ag-icon ${IconName.Trash}"/>`,
      };
    },
    [open, allowCancelOrder]
  );

  return {
    /** Get a menu item for force-cancelling the quote (by resolving and force-cancelling the order matched by RFQID) */
    getForceCancelCareOrderChildQuoteItem,
    /** Get a menu item for force-cancelling the order */
    getForceCancelCareOrderChildOrderItem,
    /** The dialog component to render. The user needs to confirm their action before we do anything. */
    dialog: <CancelCareOrderChildOrderDialog order={disclosure.context} {...disclosure} />,
  };
};

const CancelCareOrderChildOrderDialog = ({ order, ...dialogProps }: DialogProps & { order: Order | undefined }) => {
  const { orgApiEndpoint } = useEndpointsContext();
  const mixpanel = useMixpanel();
  const { add: addToast } = useGlobalToasts();
  const { forceCancel } = useOrderInteractions();

  const [trade, setTrade] = useState<Trade>();

  // When the order is set, grab trade details from that order. We only care about the first trade, there should only be one in this flow.
  useEffect(() => {
    if (order) {
      request<DataResponse<Trade>>('GET', `${orgApiEndpoint}/trades?OrderID=${order.OrderID}`)
        .then(res => setTrade(res.data.at(0)))
        .catch((e: ErrorEvent) => {
          addToast({
            variant: 'NEGATIVE',
            text: `Failed to get Trade from for OrderID: ${e.message}`,
          });
        });
    }
  }, [orgApiEndpoint, order, addToast]);

  // Clear trade state on close of dialog
  useEffect(() => {
    if (!dialogProps.isOpen) {
      setTrade(undefined);
    }
  }, [dialogProps.isOpen]);

  const handleConfirm = useCallback(() => {
    mixpanel.track(MixpanelEvent.ForceCancelOrder);
    if (!order) {
      return;
    }

    forceCancel(order.OrderID)
      .then(() => {
        addToast({
          text: 'Successfully canceled order.',
          variant: 'POSITIVE',
        });
      })
      .catch(e => {
        addToast({
          text: `Failed to cancel order: ${e.message}`,
          variant: 'NEGATIVE',
        });
      });
  }, [addToast, order, forceCancel, mixpanel]);

  const copyIDsValue = trade ? `Market Trade ID: ${trade.MarketTradeID}, Market Order ID: ${trade.MarketOrderID}` : '';

  return (
    <Dialog
      {...dialogProps}
      confirmLabel="Confirm cancellation"
      cancelLabel="Close"
      onConfirm={handleConfirm}
      title="Cancel Order"
      width="500px"
      stretchButtons
    >
      <VStack w="100%" gap="spacing12">
        <Text color="colorTextImportant" textAlign="left">
          Only cancel this order if you have confirmed with the LP that this order is canceled on their side as well.
        </Text>
        <Card p="spacing6" w="100%">
          <Flex flexDirection="column" w="100%" gap="spacing6">
            <HStack justifyContent="space-between">
              <Text fontWeight="fontWeightBold">IDs</Text>
              <CopyButton value={copyIDsValue} disabled={trade == null}>
                Copy
              </CopyButton>
            </HStack>
            <Divider />
            <HStack justifyContent="space-between">
              <Text>Market Trade ID</Text>
              <Text color="colorTextImportant">{trade?.MarketTradeID}</Text>
            </HStack>
            <HStack justifyContent="space-between">
              <Text>Market Order ID</Text>
              <Text color="colorTextImportant">{trade?.MarketOrderID}</Text>
            </HStack>
          </Flex>
        </Card>
      </VStack>
    </Dialog>
  );
};
